// Cómo se describe el Catálogo ante el agente (ADR-2026-10-14-el-agente-arma-el-catalogo-desde-
// la-pantalla): la ficha de PANTALLA ACTUAL. Sin campos (se trabaja con las herramientas).
//  • `estado` va en CADA turno, recortado a 600 caracteres: lo justo para orientarse.
//  • `notas` llega entero con `ver_pantalla`: los productos con sus ids, datos y tomas, las
//    referencias y las tomas con su instrucción.
// Puro.
import type { DescripcionDePantalla } from '../../../../../chat-atlas';
import { resumenFicha } from '../../../../../lib/fichaDeProducto';
import { tieneMedidasCompletas } from '../../../../../lib/medidas';
import { QUE_HACE_EL_MODO } from '../../../puente/camposDeModo';
import { costoDelPlan, costoPorLanzar, imagenesPorLanzar, queFalta, totalImagenes, type ItemDelPlan, type PlanDeCatalogo } from '../plan';

export const PANTALLA = 'el modo Catálogo';

export interface DatosParaDescribir {
  ruta: string;
  plan: PlanDeCatalogo | null;
  enSesion: boolean;
  escribiendo: boolean;
  generando: boolean;
  /** Productos (varianteId) cuyos datos no se pudieron guardar en Mi marca. */
  sinGuardar?: string[];
}

const ESTADO_CORTO: Record<string, string> = {
  'sin-prompt': 'sin prompt',
  escrito: 'prompt listo',
  generando: 'generando',
  generada: 'generada',
  fallo: 'falló',
};

/** Lo pendiente de un ítem ya generado: una edición o un rehacer que Generar va a lanzar. */
const pendiente = (i: ItemDelPlan): string =>
  i.edicion
    ? `, edición pendiente: ${i.edicion.instruccion.slice(0, 80)}`
    : i.rehacer
      ? ', rehacer pendiente'
      : porRegenerar(i)
        ? ', TIENE FOTO del prompt anterior: Generar la regenera'
        : '';

/** Una toma con foto cuyo prompt cambió: Generar la vuelve a hacer (no es una foto nueva). */
const porRegenerar = (i: ItemDelPlan): boolean => i.estado !== 'generada' && i.estado !== 'generando' && (i.imageIds?.length ?? 0) > 0;

export function describirCatalogo(d: DatosParaDescribir): DescripcionDePantalla {
  const queHace = QUE_HACE_EL_MODO['catalogo-agente'] ?? 'El catálogo de una marca de Hogar y Decoración armado con el agente.';
  if (!d.plan) {
    return {
      pantalla: PANTALLA,
      ruta: d.ruta,
      queHace,
      campos: [],
      faltaParaGenerar: ['elegir productos y contar qué catálogo se quiere (la portada)'],
      estado: d.enSesion ? 'La sesión está cargando.' : 'La portada del Catálogo: la persona elige productos y te escribe; con eso se crea la sesión. Todavía no hay plan.',
    };
  }
  const plan = d.plan;
  const faltas = queFalta(plan);
  const sinDatos = plan.productos.filter((p) => !tieneMedidasCompletas(p.medidas) && p.ficha.campos.length === 0).length;
  const items = plan.productos.flatMap((p) => p.items);
  const escritos = items.filter((i) => i.estado === 'escrito' || i.estado === 'generada' || i.estado === 'generando').length;
  const fijados = items.filter((i) => i.fijado).length;
  const generadas = items.filter((i) => i.estado === 'generada').length;
  const aRegenerar = items.filter(porRegenerar).length;
  const sinGuardar = plan.productos.filter((p) => d.sinGuardar?.includes(p.varianteId)).map((p) => p.nombre);
  const estado = [
    // Primero: el estado se recorta a 600 y esto es lo que el agente tiene que contar.
    sinGuardar.length
      ? `OJO: los datos de ${sinGuardar.join(', ')} no se pudieron guardar en el producto (siguen en este catálogo, en Mi marca no): avisale a la persona; la pantalla tiene "Volver a intentar".`
      : '',
    `«${plan.titulo}»: ${plan.productos.length} producto${plan.productos.length === 1 ? '' : 's'}${sinDatos ? ` (${sinDatos} sin datos)` : ''}; tomas: ${plan.tomas.length ? plan.tomas.map((t) => `${t.titulo}${t.cantidad > 1 ? ` ×${t.cantidad}` : ''}`).join(', ') : 'ninguna'}.`,
    `Prompts: ${escritos}/${items.length} listos${fijados ? `, ${fijados} fijados` : ''}${generadas ? `, ${generadas} generadas` : ''}${aRegenerar ? `, ${aRegenerar} con foto vieja por regenerar` : ''}. Formato ${plan.formato}, calidad ${plan.resolucion}, ${totalImagenes(plan)} imágenes = ${costoDelPlan(plan)} créditos; ${imagenesPorLanzar(plan)} por lanzar = ${costoPorLanzar(plan)} créditos.`,
    plan.estilo.trim() ? `Estilo: ${plan.estilo.trim().slice(0, 120)}${plan.estilo.length > 120 ? '…' : ''}.` : 'Sin estilo común todavía.',
    d.escribiendo ? 'El escritor está escribiendo.' : d.generando ? 'Generando.' : faltas.length ? `Falta: ${faltas.slice(0, 2).join('; ')}${faltas.length > 2 ? '…' : ''}.` : 'Listo para generar (espera el click).',
    'Ids y datos: ver_pantalla. Prompts y fotos: ver_tomas.',
  ]
    .filter(Boolean)
    .join(' ')
    .slice(0, 600);

  const notas: string[] = [
    `PRODUCTOS (id=id de producto para las herramientas; cada toma con su id de ítem producto__toma):`,
    ...plan.productos.map((p) => {
      const datos = resumenFicha(p.ficha, p.medidas) || 'sin datos';
      const tomas = p.items.map((i) => `${i.id} [${ESTADO_CORTO[i.estado]}${i.fijado ? ', fijado' : ''}${pendiente(i)}${i.motivo ? `: ${i.motivo}` : ''}]`).join('; ');
      return `- id=${p.varianteId} · ${p.nombre} · ${datos}${p.notas ? ` · notas: ${p.notas}` : ''}${p.etiquetas?.length ? ` · etiquetas de la ficha: ${p.etiquetas.join(' / ')}` : ''}${tomas ? `\n    ${tomas}` : ''}`;
    }),
    `TOMAS:`,
    ...plan.tomas.map(
      (t) =>
        `- ${t.id} (${t.titulo}, ícono ${t.icono}, ×${t.cantidad}${t.fondoBlanco ? ', fondo blanco' : ''}${t.tarea ? `, tarea ${t.tarea}` : ''}${
          t.infografia ? `, infografía de medidas (plantilla de ficha técnica), fondo ${t.colorInfografia ?? 'blanco'}${t.pillsDeLaFicha === false ? ', sin etiquetas' : ', con la ficha como etiquetas'}${t.estiloGrafico ? `, estilo gráfico: ${t.estiloGrafico}` : ''}` : ''
        }): ${t.queMuestra}${t.aplicaA === 'todos' ? '' : ` [sólo ${t.aplicaA.length} producto${t.aplicaA.length === 1 ? '' : 's'}]`}`,
    ),
    ...(plan.referencias.length ? [`REFERENCIAS (imágenes que subió la persona): ${plan.referencias.map((r) => `${r.id} (${r.etiqueta})`).join(', ')}`] : []),
    `PEDIDO ORIGINAL: ${plan.pedido || '(vacío)'}`,
    `ESTILO COMÚN: ${plan.estilo || '(vacío)'}`,
  ];

  return {
    pantalla: PANTALLA,
    ruta: d.ruta,
    queHace,
    campos: [],
    faltaParaGenerar: faltas,
    costo: `${costoDelPlan(plan)} créditos (${totalImagenes(plan)} imágenes en ${plan.resolucion})`,
    estado,
    notas,
  };
}
